import React, { useMemo, useState } from 'react'
import { SoundWaveIcon } from '@components/SideBars/SpaceSideBar/SoundWaveIcon'
import { Box, Button, CardOpener, MotionBox, Paragraph, RoundIconButton, Stack } from '@ui'
import { AnimatePresence } from 'framer-motion'

import { LiveUsersPill } from './LiveUsersPill'
import { shouldShowMicPopup } from './liveUtils'
import { MicControlButton } from './MicControlButton'
import { MicInfoPopup } from './MicInfoPopup'
import { useLiveSessionState } from './useLiveSessionState'

export const LiveSessionFooter = React.memo(() => {
    // Using useState for the ref to trigger re-render when mounted, ensuring CardOpener sees the element
    const [footerElement, setFooterElement] = useState<HTMLElement | null>(null)
    const footerRef = {
        current: footerElement as HTMLDivElement,
    } as React.RefObject<HTMLDivElement>
    const {
        userId,
        spaceId,
        channelId,
        spaceName,
        channelName,
        isCurrentDeviceLive,
        otherLiveDevice,
        channelState,
        audioState,
        isAudioConnected,
        isOnStage,
        isRequestingToJoinStage,
        isLive,
        hasListeners,
        hasStageRequests,
        connectionStatus,
        handleLeave,
        handleListen,
        handleJoinStage,
        handleSwitchHere,
        handleModerateStage,
        handleToggleMute,
        handleSetPreferredDevice,
        handleSetOutputDevice,
    } = useLiveSessionState('LiveSessionFooter')

    // Audio status indicator
    const audioStatusText = useMemo(() => {
        switch (audioState?.status) {
            case undefined:
                if (isCurrentDeviceLive && isOnStage) {
                    console.log('[LiveSessionFooter ws>] Audio status: undefined')
                    return 'Joining...'
                } else {
                    return null
                }
            case 'initializing':
                console.log('[LiveSessionFooter ws>] Audio status: initializing')
                return 'Connecting audio...'
            case 'error':
                console.log('[LiveSessionFooter ws>] Audio status: error', audioState?.error)
                return `Audio error: ${audioState?.error?.message}`
            default:
                return null
        }
    }, [audioState?.status, audioState?.error, isCurrentDeviceLive, isOnStage])

    // Don't show footer if no device is live
    if (!isCurrentDeviceLive && !otherLiveDevice) {
        return null
    }
    return (
        <Box padding="xs">
            <Box
                horizontal
                elevate
                padding
                ref={setFooterElement}
                rounded="sm"
                gap="md"
                alignItems="center"
                width="100%"
                height="x9"
            >
                {/* Sound wave icon and channel info */}
                <Stack horizontal gap alignItems="center">
                    <SoundWaveIcon
                        size={32}
                        animate={isCurrentDeviceLive}
                        variant="inverted"
                        status={connectionStatus}
                    />
                    <Stack horizontal gap="none">
                        <Paragraph strong truncate color="default">
                            {spaceName && `${spaceName} - `}
                            {channelName ?? 'Live session'}
                        </Paragraph>
                    </Stack>
                </Stack>

                {/* Stage users pill */}
                {isLive && spaceId && channelId && channelState && (
                    <LiveUsersPill
                        type="stage"
                        userIds={channelState.stage.userIds}
                        totalCount={channelState.stage.totalCount}
                        speakingUserIds={audioState?.speakingUsers}
                        cursor={channelState.stage.cursor}
                        isOnStage={isOnStage}
                        stageRequests={channelState.stageRequests}
                        spaceId={spaceId}
                        channelId={channelId}
                        channelName={channelName}
                        stageSize={channelState.stageSize}
                        currentUserId={userId}
                        onModerateStage={handleModerateStage}
                    />
                )}

                {/* Listeners pill */}
                {isLive &&
                    (hasListeners || hasStageRequests) &&
                    spaceId &&
                    channelId &&
                    channelState && (
                        <LiveUsersPill
                            type="listeners"
                            userIds={channelState.listeners.userIds}
                            totalCount={channelState.listeners.totalCount}
                            cursor={channelState.listeners.cursor}
                            stageRequests={channelState.stageRequests}
                            isOnStage={isOnStage}
                            spaceId={spaceId}
                            channelId={channelId}
                            channelName={channelName}
                            currentUserId={userId}
                            onModerateStage={handleModerateStage}
                        />
                    )}

                {/* Audio status indicator */}
                {audioStatusText && <Paragraph size="sm">{audioStatusText}</Paragraph>}

                {/* Action buttons row - justify end */}
                <Stack horizontal grow gap justifyContent="end" alignItems="center">
                    <AnimatePresence>
                        {/* Request Stage button - only show when listening (not on stage) */}
                        {isCurrentDeviceLive && !isOnStage && !isRequestingToJoinStage && (
                            <AnimatedActionButton key="stage-action">
                                <RoundIconButton
                                    icon="blockHand"
                                    label="Request Stage"
                                    background="level3"
                                    onClick={() => handleJoinStage()}
                                />
                            </AnimatedActionButton>
                        )}

                        {/* Stage Requested button - show when request is pending */}
                        {isCurrentDeviceLive && !isOnStage && isRequestingToJoinStage && (
                            <AnimatedActionButton key="stage-action">
                                <RoundIconButton
                                    disabled
                                    icon="blockHand"
                                    label="Stage Requested"
                                    background="positive"
                                    iconColor="level2"
                                />
                            </AnimatedActionButton>
                        )}

                        {/* Mute/Unmute button - only when on stage and audio connected */}
                        {isLive && isOnStage && isAudioConnected && audioState && (
                            <AnimatedActionButton key="mute">
                                {shouldShowMicPopup(audioState.mic) ? (
                                    <CardOpener
                                        placement="vertical"
                                        overrideTriggerRef={footerRef}
                                        render={
                                            <Box
                                                padding
                                                rounded="sm"
                                                boxShadow="dropdown"
                                                background="level1"
                                            >
                                                <MicInfoPopup
                                                    micState={audioState.mic}
                                                    onDeviceSelect={handleSetPreferredDevice}
                                                    onOutputDeviceSelect={handleSetOutputDevice}
                                                />
                                            </Box>
                                        }
                                    >
                                        {({ triggerProps }) => (
                                            <MicControlButton
                                                isMuted={audioState.localMuted}
                                                containerProps={triggerProps}
                                                onToggleMute={() =>
                                                    handleToggleMute(!audioState.localMuted)
                                                }
                                            />
                                        )}
                                    </CardOpener>
                                ) : (
                                    <MicControlButton
                                        isMuted={audioState.localMuted}
                                        onToggleMute={() =>
                                            handleToggleMute(!audioState.localMuted)
                                        }
                                    />
                                )}
                            </AnimatedActionButton>
                        )}

                        {/* Leave Stage button - only show when on stage (shares key with join-stage for swap animation) */}
                        {isCurrentDeviceLive && isOnStage && (
                            <AnimatedActionButton key="stage-action">
                                <RoundIconButton
                                    icon="leaveStage"
                                    label="Leave Stage"
                                    background="level3"
                                    onClick={() => handleListen()}
                                />
                            </AnimatedActionButton>
                        )}
                    </AnimatePresence>

                    {/* Leave channel button - red/danger (fixed position) */}
                    {isCurrentDeviceLive && (
                        <RoundIconButton
                            icon="phoneHangup"
                            label="Disconnect"
                            background="negative"
                            onClick={() => handleLeave()}
                        />
                    )}

                    {/* Switch here button - when live on another device */}
                    {!isCurrentDeviceLive && otherLiveDevice && (
                        <>
                            <Paragraph color="gray2">Listening on another device</Paragraph>
                            <Button
                                tone="level2"
                                size="button_x5"
                                onClick={() => handleSwitchHere()}
                            >
                                Switch Here
                            </Button>
                        </>
                    )}
                </Stack>
            </Box>
        </Box>
    )
})

/** Wrapper for action buttons - fade in on enter, instant removal on exit */
const AnimatedActionButton = ({ children }: { children: React.ReactNode }) => (
    <MotionBox
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1, transition: { duration: 0.15 } }}
        exit={{ opacity: 0, scale: 0.8, transition: { duration: 0 } }}
    >
        {children}
    </MotionBox>
)

LiveSessionFooter.displayName = 'LiveSessionFooter'
